import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/Button/Button';
import { TextField } from '@/components/TextField/TextField';
import { ApiError, NetworkError } from '@/lib/api';
import { t } from '@/lib/i18n';
import { AddHumanBodySchema, HUMAN_ADD_ROLES, type AddHumanBody } from '../../schemas';
import { useAddHuman } from '../../hooks';
import type { Member } from '../../schemas';
import styles from './HireHumanForm.module.css';

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  editor: 'Editor',
  contributor: 'Contributor',
  viewer: 'Viewer',
};

interface HireHumanFormProps {
  onSuccess: (member: Member) => void;
  onCancel: () => void;
}

export function HireHumanForm({ onSuccess, onCancel }: HireHumanFormProps) {
  const addHuman = useAddHuman();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AddHumanBody>({
    resolver: zodResolver(AddHumanBodySchema),
    defaultValues: { role_slug: 'contributor' },
  });

  function onSubmit(data: AddHumanBody) {
    setFormError(null);
    addHuman.mutate(data, {
      onSuccess: (res) => onSuccess(res.member),
      onError: (err) => {
        if (err instanceof ApiError) {
          if (err.code === 'CONFLICT') {
            setFormError(t('A user with that email already exists.'));
          } else if (err.status === 403) {
            setFormError(t("You don't have permission to add members."));
          } else {
            setFormError(err.message);
          }
        } else if (err instanceof NetworkError) {
          setFormError(t("Couldn't reach the server. Check your connection and try again."));
        } else {
          setFormError(t('Something went wrong. Please try again.'));
        }
      },
    });
  }

  return (
    <div className={styles.page} data-testid="hire-human-form">
      <div className={styles.devBadge}>DEV ONLY</div>
      <h1 className={styles.title}>{t('Add a human user')}</h1>
      <p className={styles.subtitle}>
        {t(
          'Available because email verification is disabled. Creates a verified account and adds the user to your team.',
        )}
      </p>
      <form className={styles.form} onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
        <div className={styles.nameRow}>
          <TextField
            label={t('First name')}
            error={errors.first_name?.message}
            {...register('first_name')}
          />
          <TextField
            label={t('Last name')}
            error={errors.last_name?.message}
            {...register('last_name')}
          />
        </div>
        <TextField
          label={t('Email')}
          type="email"
          error={errors.email?.message}
          {...register('email')}
        />
        <TextField
          label={t('Password')}
          type="password"
          error={errors.password?.message}
          {...register('password')}
        />
        <label className={styles.selectField}>
          <span className={styles.selectLabel}>{t('Role')}</span>
          <select className={styles.select} {...register('role_slug')} aria-label={t('Role')}>
            {HUMAN_ADD_ROLES.map((slug) => (
              <option key={slug} value={slug}>
                {ROLE_LABELS[slug]}
              </option>
            ))}
          </select>
          {errors.role_slug ? (
            <span className={styles.fieldError}>{errors.role_slug.message}</span>
          ) : null}
        </label>
        {formError ? <p className={styles.formError}>{formError}</p> : null}
        <div className={styles.actions}>
          <Button type="button" variant="ghost" size="md" onClick={onCancel}>
            {t('← Back')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={isSubmitting || addHuman.isPending}
            isLoading={addHuman.isPending}
          >
            {t('Add user')} →
          </Button>
        </div>
      </form>
    </div>
  );
}
